/**
 * Custom Form Handler
 * Handles file validation, product selection, and form interactions
 * Includes accessibility, error handling, and form persistence features
 */
window.CustomForm = (function () {
  "use strict";

  let config = {};
  let formData = {};

  /**
   * Form state management
   */
  const FormState = {
    init: function () {
      this.loadFormData();
      this.setupFormPersistence();
      this.setupFormValidation();
    },

    loadFormData: function () {
      try {
        const saved = localStorage.getItem("customForm_" + config.sectionId);
        if (saved) {
          formData = JSON.parse(saved);
          this.restoreFormData();
        }
      } catch (e) {
        console.warn("Failed to load saved form data:", e);
      }
    },

    saveFormData: function () {
      if (!config.sectionId) return;
      try {
        const form = document.getElementById("form-" + config.sectionId);
        if (!form) return;

        const data = {};
        const formDataObj = new FormData(form);
        for (let [key, value] of formDataObj.entries()) {
          if (
            !key ||
            !key.trim() ||
            key.startsWith("g-recaptcha") ||
            key === "mode"
          )
            continue;
          data[key] = value;
        }

        localStorage.setItem(
          "customForm_" + config.sectionId,
          JSON.stringify(data),
        );
      } catch (e) {
        console.warn("Failed to save form data:", e);
      }
    },

    restoreFormData: function () {
      const form = document.getElementById("form-" + config.sectionId);
      if (!form) return;

      Object.entries(formData).forEach(([name, value]) => {
        const field = form.querySelector(`[name="${name}"]`);
        if (field && field.type !== "file") {
          if (field.type === "checkbox" || field.type === "radio") {
            field.checked = field.value === value;
          } else {
            field.value = value;
          }
        }
      });
    },

    clearFormData: function () {
      try {
        localStorage.removeItem("customForm_" + config.sectionId);
      } catch (e) {
        console.warn("Failed to clear form data:", e);
      }
    },

    setupFormPersistence: function () {
      const form = document.getElementById("form-" + config.sectionId);
      if (!form) return;

      // Save form data on input changes
      form.addEventListener(
        "input",
        this.debounce(() => {
          this.saveFormData();
        }, 1000),
      );

      form.addEventListener("change", () => {
        this.saveFormData();
      });
    },

    setupFormValidation: function () {
      const form = document.getElementById("form-" + config.sectionId);
      if (!form) return;

      form.addEventListener("invalid", this.handleInvalidField, true);
      form.addEventListener("submit", this.handleFormSubmit.bind(this));

      // Setup real-time validation
      this.setupRealTimeValidation(form);

      // Setup submit button state management
      this.setupSubmitButtonWatcher();
    },

    setupRealTimeValidation: function (form) {
      // Disable browser's default validation UI
      form.setAttribute("novalidate", "novalidate");

      // Add unified event listeners to all form fields
      form.querySelectorAll("input, textarea, select").forEach((field) => {
        // Clear errors on input
        field.addEventListener("input", (e) => {
          this.clearFieldError(e.target);
          if (e.target.setCustomValidity) {
            e.target.setCustomValidity("");
          }
        });

        // Validate on blur
        field.addEventListener("blur", (e) => {
          this.validateFieldRealTime(e.target);
        });

        // Special handling for file inputs
        if (field.type === "file") {
          field.addEventListener("change", (e) => {
            this.validateFieldRealTime(e.target);
          });
        }
      });
    },

    validateFieldRealTime: function (field) {
      // Skip validation for radio buttons (handled at group level)
      if (field.type === "radio") return;

      const result = this.validateField(field);

      if (field.value.trim() && !result.isValid) {
        this.showFieldError(field, result.message);
      } else if (field.value.trim() && result.isValid) {
        this.showFieldSuccess(field);
      }
    },

    showFieldSuccess: function (field) {
      const fieldContainer = field.closest(
        ".field, .radio__field, .checkbox__field",
      );
      if (fieldContainer) {
        fieldContainer.classList.remove("field--invalid");
        fieldContainer.classList.add("field--valid");

        // Clear the error container if it exists
        const errorContainer = fieldContainer.querySelector(
          ".field__error-container",
        );
        if (errorContainer) {
          errorContainer.innerHTML = "";
          errorContainer.removeAttribute("role");
        } else {
          // Fallback: remove error messages from the field container
          const errorElements = fieldContainer.querySelectorAll(
            ".field__error, .upload__status--error",
          );
          errorElements.forEach((element) => element.remove());
        }
      } else {
        // Fallback: remove from parent element
        const errorElements = field.parentElement.querySelectorAll(
          ".field__error, .upload__status--error",
        );
        errorElements.forEach((element) => element.remove());
      }
    },

    clearFieldError: function (field) {
      const fieldContainer = field.closest(
        ".field, .radio__field, .checkbox__field",
      );
      if (fieldContainer) {
        fieldContainer.classList.remove("field--invalid");
        fieldContainer.classList.remove("field--valid");

        // Clear the error container if it exists
        const errorContainer = fieldContainer.querySelector(
          ".field__error-container",
        );
        if (errorContainer) {
          errorContainer.innerHTML = "";
          errorContainer.removeAttribute("role");
        } else {
          // Fallback: remove error messages from the field container
          const errorElements = fieldContainer.querySelectorAll(
            ".field__error, .upload__status--error",
          );
          errorElements.forEach((element) => element.remove());
        }
      } else {
        // Fallback: remove from parent element
        const errorElements = field.parentElement.querySelectorAll(
          ".field__error, .upload__status--error",
        );
        errorElements.forEach((element) => element.remove());
      }

      // Clear any custom validity messages
      if (field.setCustomValidity) {
        field.setCustomValidity("");
      }
    },

    handleInvalidField: function (event) {
      event.preventDefault();
      const field = event.target;

      // Get the form element from the field
      const form = field.form;
      // Focus first invalid field
      if (!form.querySelector(".field--invalid")) {
        field.closest(".field")?.classList.add("field--invalid");
        field.focus();

        // Announce error to screen readers
        const errorMsg = field.validationMessage || "This field is required";
        FormState.announceError(field, errorMsg);
      }
    },

    handleFormSubmit: function (event) {
      const form = event.target;

      // Clear previous errors
      form.querySelectorAll(".field--invalid").forEach((field) => {
        field.classList.remove("field--invalid");
      });

      // Custom validation
      if (!this.validateForm(form)) {
        event.preventDefault();
        return false;
      }

      this.showLoadingState(true);
    },

    validateForm: function (form) {
      const errors = [];

      // Clear all previous field errors
      this.clearAllErrors(form);

      // Get all form fields and validate them
      const fields = form.querySelectorAll("input, textarea, select");
      let isValid = true;

      fields.forEach((field) => {
        const result = this.validateField(field);
        if (!result.isValid) {
          this.showFieldError(field, result.message);
          errors.push(result.message);
          isValid = false;
        }
      });

      // Validate radio groups separately
      if (!this.validateRadioGroups(form, errors)) {
        isValid = false;
      }

      // Focus first invalid field
      if (!isValid) {
        const firstInvalid = form.querySelector(
          ".field--invalid input, .field--invalid textarea, .field--invalid select",
        );
        if (firstInvalid) {
          firstInvalid.focus();
          this.announceError(
            firstInvalid,
            errors[0] || "Please correct the errors in the form",
          );
        }
      }

      return isValid;
    },

    validateField: function (field) {
      const value = field.value.trim();
      const isRequired = field.hasAttribute("required");
      const fieldName = this.getFieldLabel(field);
      const fieldType = field.type || field.tagName.toLowerCase();

      // Required validation (skip for radio buttons - handled separately)
      if (isRequired && !value && fieldType !== "radio") {
        return { isValid: false, message: `${fieldName} is required` };
      }

      // Skip further validation if empty and not required
      if (!value && !isRequired) {
        return { isValid: true };
      }

      // Type-specific validation
      switch (fieldType) {
        case "email":
          return this.validateEmail(value);
        case "file":
          return this.validateFile(field);
        case "date":
          return this.validateDate(value, fieldName);
        case "text":
        case "textarea":
          return this.validateTextInput(field, value, fieldName);
        case "checkbox":
          return {
            isValid: !isRequired || field.checked,
            message: `${fieldName} must be checked`,
          };
        default:
          return { isValid: true };
      }
    },

    validateEmail: function (value) {
      return {
        isValid: this.isValidEmail(value),
        message: "Please enter a valid email address",
      };
    },

    validateFile: function (field) {
      const files = field.files;
      const fieldName = this.getFieldLabel(field);

      if (!files || files.length === 0) {
        return field.hasAttribute("required")
          ? { isValid: false, message: `${fieldName} is required` }
          : { isValid: true };
      }

      const file = files[0];
      const maxSizeMB = parseInt(field.getAttribute("data-max-size")) || 5;
      const maxSizeBytes = maxSizeMB * 1024 * 1024;

      // Size validation
      if (file.size > maxSizeBytes) {
        const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1);
        return {
          isValid: false,
          message: `File size (${fileSizeMB}MB) exceeds the ${maxSizeMB}MB limit`,
        };
      }

      // Type validation
      const acceptTypes = (field.getAttribute("accept") || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      if (acceptTypes.length > 0) {
        const isValidType = acceptTypes.some((type) =>
          type.endsWith("/*")
            ? file.type.startsWith(type.slice(0, type.indexOf("/")))
            : file.type === type,
        );

        if (!isValidType) {
          return {
            isValid: false,
            message: `File type not supported. Please upload: ${this.getFriendlyTypeNames(acceptTypes)}`,
          };
        }
      }

      return { isValid: true };
    },

    validateDate: function (value, fieldName) {
      const selectedDate = new Date(value);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (isNaN(selectedDate.getTime())) {
        return { isValid: false, message: "Please enter a valid date" };
      }

      const oneYearAgo = new Date(today);
      oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);

      if (selectedDate < oneYearAgo) {
        return {
          isValid: false,
          message: "Date cannot be more than 1 year in the past",
        };
      }

      const fiveYearsFromNow = new Date(today);
      fiveYearsFromNow.setFullYear(fiveYearsFromNow.getFullYear() + 5);

      if (selectedDate > fiveYearsFromNow) {
        return {
          isValid: false,
          message: "Date cannot be more than 5 years in the future",
        };
      }

      return { isValid: true };
    },

    validateTextInput: function (field, value, fieldName) {
      // Length validation
      const minLength = field.getAttribute("minlength");
      const maxLength = field.getAttribute("maxlength");

      if (minLength && value.length < parseInt(minLength)) {
        return {
          isValid: false,
          message: `${fieldName} must be at least ${minLength} characters long`,
        };
      }

      if (maxLength && value.length > parseInt(maxLength)) {
        return {
          isValid: false,
          message: `${fieldName} cannot exceed ${maxLength} characters`,
        };
      }

      return { isValid: true };
    },

    validateRadioGroups: function (form, errors) {
      const radioGroups = new Map();

      // Group radios by name
      form.querySelectorAll('input[type="radio"]').forEach((input) => {
        if (!radioGroups.has(input.name)) {
          radioGroups.set(input.name, []);
        }
        radioGroups.get(input.name).push(input);
      });

      let isValid = true;
      radioGroups.forEach((radios, groupName) => {
        const isRequired = radios.some((radio) =>
          radio.hasAttribute("required"),
        );
        const isChecked = radios.some((radio) => radio.checked);

        if (isRequired && !isChecked) {
          const fieldName = this.getFieldLabel(radios[0]) || groupName;
          this.showFieldError(
            radios[0],
            `Please select an option for ${fieldName}`,
          );
          errors.push(`${fieldName} selection is required`);
          isValid = false;
        }
      });

      return isValid;
    },

    clearAllErrors: function (form) {
      form
        .querySelectorAll(".field--invalid, .field--valid")
        .forEach((field) => {
          field.classList.remove("field--invalid", "field--valid");
        });
      form
        .querySelectorAll(".field__error, .upload__status--error")
        .forEach((error) => {
          error.remove();
        });
    },

    // Simplified validation helpers
    isValidEmail: function (email) {
      const input = document.createElement("input");
      input.type = "email";
      input.value = email;
      return input.checkValidity();
    },

    isValidPhone: function (phone) {
      const digits = phone.replace(/\D/g, "");
      return digits.length >= 7 && digits.length <= 15;
    },

    isValidPostalCode: function (code) {
      const patterns = [
        /^\d{5}(-?\d{4})?$/, // US ZIP
        /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/, // Canadian
        /^[A-Z]{1,2}[0-9][A-Z0-9]? ?[0-9][A-Z]{2}$/i, // UK
      ];
      return patterns.some((pattern) => pattern.test(code));
    },

    getFieldLabel: function (input) {
      const label = document.querySelector(`label[for="${input.id}"]`);
      return label
        ? label.textContent.replace(/\*/g, "").trim()
        : input.getAttribute("aria-label") ||
            input.getAttribute("placeholder") ||
            (input.name || "Field").replace(/[_-]/g, " ");
    },

    getFriendlyTypeNames: function (mimeTypes) {
      const typeMap = {
        "image/png": "PNG images",
        "image/jpeg": "JPG images",
        "image/jpg": "JPG images",
        "image/svg+xml": "SVG images",
        "image/gif": "GIF images",
        "application/pdf": "PDF documents",
        "image/*": "Image files",
      };

      return mimeTypes
        .map((type) => typeMap[type.trim()] || type.trim())
        .join(", ");
    },

    showFieldError: function (field, message) {
      const fieldContainer = field.closest(
        ".field, .radio__field, .checkbox__field",
      );
      if (fieldContainer) {
        fieldContainer.classList.add("field--invalid");
        fieldContainer.classList.remove("field--valid");
      }

      // Find the error container for this field
      const errorContainer = fieldContainer
        ? fieldContainer.querySelector(".field__error-container")
        : null;

      if (errorContainer) {
        // Use the dedicated error container
        errorContainer.innerHTML = `<div class="field__error">${message}</div>`;
        errorContainer.setAttribute("role", "alert");
      } else {
        // Fallback for fields without containers (like radio groups)
        this.showFieldErrorFallback(field, fieldContainer, message);
      }
    },

    showFieldErrorFallback: function (field, fieldContainer, message) {
      // Remove existing error messages
      const existingError = fieldContainer
        ? fieldContainer.querySelector(".field__error, .upload__status--error")
        : field.parentElement.querySelector(
            ".field__error, .upload__status--error",
          );
      if (existingError) {
        existingError.remove();
      }

      // Create new error element for fields without error containers
      const errorDiv = document.createElement("div");
      errorDiv.className = "field__error";
      errorDiv.textContent = message;
      errorDiv.setAttribute("role", "alert");
      errorDiv.setAttribute("aria-live", "polite");

      if (fieldContainer) {
        fieldContainer.appendChild(errorDiv);
      } else {
        field.parentElement.appendChild(errorDiv);
      }
    },

    announceError: function (field, message) {
      // Create or update aria-live region
      let announcer = document.getElementById("form-announcer");
      if (!announcer) {
        announcer = document.createElement("div");
        announcer.id = "form-announcer";
        announcer.className = "sr-only";
        announcer.setAttribute("aria-live", "polite");
        announcer.setAttribute("aria-atomic", "true");
        document.body.appendChild(announcer);
      }
      announcer.textContent = message;
    },

    showLoadingState: function (show) {
      const submitBtn = document.getElementById("submit-" + config.sectionId);
      if (!submitBtn) return;

      const buttonText = submitBtn.querySelector(".button-text");
      const buttonLoading = submitBtn.querySelector(".button-loading");

      if (show) {
        submitBtn.disabled = true;
        submitBtn.setAttribute("aria-busy", "true");
        if (buttonText) buttonText.style.display = "none";
        if (buttonLoading) buttonLoading.style.display = "inline-flex";
      } else {
        submitBtn.disabled = false;
        submitBtn.setAttribute("aria-busy", "false");
        if (buttonText) buttonText.style.display = "inline";
        if (buttonLoading) buttonLoading.style.display = "none";
      }
    },

    debounce: function (func, wait) {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    },

    // Submit button state management
    updateSubmitButtonState: function () {
      if (!config.sectionId) return;

      const form = document.getElementById("form-" + config.sectionId);
      const submitBtn = document.getElementById("submit-" + config.sectionId);

      if (!form || !submitBtn) return;

      const shouldEnable = this.isFormReadyToSubmit(form);
      submitBtn.disabled = !shouldEnable;
    },

    isFormReadyToSubmit: function (form) {
      // Check all required fields are filled and valid
      const requiredFields = form.querySelectorAll(
        "input[required], textarea[required], select[required]",
      );

      for (const field of requiredFields) {
        // Skip terms modal checkbox - handled separately
        if (field.id === "accept-terms") continue;

        if (field.type === "radio") {
          // Check if any radio in this group is selected
          const radioGroup = form.querySelectorAll(
            `input[type="radio"][name="${field.name}"]`,
          );
          const hasSelection = Array.from(radioGroup).some(
            (radio) => radio.checked,
          );
          if (!hasSelection) {
            return false;
          }
        } else if (field.type === "checkbox") {
          if (!field.checked) {
            return false;
          }
        } else if (field.type === "file") {
          if (!field.files || field.files.length === 0) {
            return false;
          }
        } else {
          // Text, email, date, etc.
          if (!field.value.trim()) {
            return false;
          }
          if (!field.checkValidity()) {
            return false;
          }
        }
      }

      // Check terms acceptance if modal exists
      const termsConfirmed = document.getElementById("terms-confirmed");
      const openTermsBtn = document.getElementById("open-terms-modal");

      // If there's a terms modal button, terms must be accepted
      if (openTermsBtn) {
        // Terms are accepted when the confirmation message is visible
        if (!termsConfirmed || termsConfirmed.style.display === "none") {
          return false;
        }
      }

      return true;
    },

    setupSubmitButtonWatcher: function () {
      if (!config.sectionId) return;

      const form = document.getElementById("form-" + config.sectionId);
      if (!form) return;

      // Bind the update function to maintain proper context
      const boundUpdate = this.updateSubmitButtonState.bind(this);

      // Update on form changes
      form.addEventListener("input", boundUpdate);
      form.addEventListener("change", boundUpdate);

      // Watch for terms confirmation
      const termsConfirmed = document.getElementById("terms-confirmed");
      if (termsConfirmed) {
        const observer = new MutationObserver(() => {
          setTimeout(boundUpdate, 10);
        });
        observer.observe(termsConfirmed, {
          attributes: true,
          attributeFilter: ["style"],
        });
      }

      // Initial state check
      setTimeout(boundUpdate, 100);
    },
  };

  /**
   * Product selector functionality
   */
  const ProductSelector = {
    init: function () {
      this.setupProductCards();
      this.setupSwatches();
      this.setupQuantityControls();
    },

    setupProductCards: function () {
      document
        .querySelectorAll(
          '.product-card input[type="radio"][name="selected_product"]',
        )
        .forEach((input) => {
          input.addEventListener("change", () =>
            this.updateProductCardSelection(input),
          );

          // Initialize selected state on load
          if (input.checked) {
            this.updateProductCardSelection(input);
          }
        });
    },

    setupSwatches: function () {
      document.querySelectorAll(".product-card").forEach((card) => {
        card.querySelectorAll(".swatch").forEach((swatch) => {
          swatch.addEventListener("click", () =>
            this.handleSwatchClick(card, swatch),
          );
          swatch.addEventListener("keydown", (e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              this.handleSwatchClick(card, swatch);
            }
          });
        });
      });
    },

    setupQuantityControls: function () {
      document.querySelectorAll(".product-qty").forEach((qtyContainer) => {
        const input = qtyContainer.querySelector(".product-qty__input");
        const btnInc = qtyContainer.querySelector(".qty-increase");
        const btnDec = qtyContainer.querySelector(".qty-decrease");
        const minQty = parseInt(input.getAttribute("min")) || 1;

        if (btnInc) {
          btnInc.addEventListener("click", () => {
            input.value = Math.max(minQty, parseInt(input.value || minQty) + 1);
          });
        }

        if (btnDec) {
          btnDec.addEventListener("click", () => {
            input.value = Math.max(minQty, parseInt(input.value || minQty) - 1);
          });
        }

        input.addEventListener("input", () => {
          if (parseInt(input.value) < minQty) {
            input.value = minQty;
          }
        });
      });
    },

    updateSwatchSelection: function (card, selectedSwatch) {
      // Reset all swatches in this card
      card.querySelectorAll(".swatch").forEach((swatch) => {
        swatch.classList.remove("selected");
        swatch.setAttribute("aria-checked", "false");
      });

      // Select the clicked swatch
      selectedSwatch.classList.add("selected");
      selectedSwatch.setAttribute("aria-checked", "true");

      // Update hidden variant input
      const hiddenInput = card.querySelector('input[name="variant_id"]');
      if (hiddenInput) {
        hiddenInput.value = selectedSwatch.dataset.variantId;
      }
    },

    updateProductCardSelection: function (selectedInput) {
      const selectedCard = selectedInput.closest(".product-card");

      // Remove selection from all other cards
      document.querySelectorAll(".product-card").forEach((card) => {
        if (card !== selectedCard) {
          card.classList.remove("selected");
          card.querySelectorAll(".swatch").forEach((swatch) => {
            swatch.classList.remove("selected");
            swatch.setAttribute("aria-checked", "false");
          });
        }
      });

      // Activate selected card
      if (selectedCard) {
        selectedCard.classList.add("selected");

        // Ensure a swatch is selected
        const currentSelectedSwatch =
          selectedCard.querySelector(".swatch.selected");
        if (!currentSelectedSwatch) {
          const firstSwatch = selectedCard.querySelector(".swatch");
          if (firstSwatch) {
            this.handleSwatchClick(selectedCard, firstSwatch);
          }
        }
      }
    },

    handleSwatchClick: function (card, swatch) {
      const variantId = swatch.dataset.variantId;
      const productTitle = swatch.dataset.productTitle;
      const variantTitle = swatch.dataset.variantTitle;
      const selectionLabel =
        productTitle && variantTitle
          ? `${productTitle} - ${variantTitle}`
          : swatch.getAttribute("aria-label") || productTitle || variantTitle;
      const radioInput = card.querySelector(
        'input[type="radio"][name="selected_product"]',
      );
      const hiddenVariantInput = card.querySelector('input[name="variant_id"]');

      // Update swatch selection
      this.updateSwatchSelection(card, swatch);

      // Update radio input
      if (radioInput) {
        radioInput.checked = true;
        if (selectionLabel) {
          radioInput.value = selectionLabel;
        }
        this.updateProductCardSelection(radioInput);
      }

      // Update hidden variant input
      if (hiddenVariantInput) {
        hiddenVariantInput.value = variantId;
      }

      // Update product image
      const productImage = card.querySelector(".product-image img");
      const imageSrc = swatch.dataset.image;
      if (imageSrc && productImage) {
        productImage.src = imageSrc;
      }
    },
  };

  /**
   * Form utilities
   */
  const FormUtils = {
    generateShortUUID: function () {
      return Math.random().toString(36).substring(2, 8).toUpperCase();
    },

    cleanupUnselectedProducts: function () {
      document.querySelectorAll(".product-card").forEach((card) => {
        const isSelected = card.querySelector(
          'input[type="radio"][name="selected_product"]',
        ).checked;
        if (!isSelected) {
          const qtyInput = card.querySelector('input[name="quantity"]');
          const variantInput = card.querySelector('input[name="variant_id"]');
          if (qtyInput) qtyInput.remove();
          if (variantInput) variantInput.remove();
        }
      });
    },

    addSubmissionUUID: function (form) {
      // Remove any existing UUID input
      const existingUUIDInput = form.querySelector(
        'input[name="submission_uuid"]',
      );
      if (existingUUIDInput) existingUUIDInput.remove();

      // Generate and add new UUID
      const shortUUID = this.generateShortUUID();
      const uuidField = document.createElement("input");
      uuidField.type = "hidden";
      uuidField.name = "submission_uuid";
      uuidField.value = shortUUID;
      form.appendChild(uuidField);

      return shortUUID;
    },
  };

  /**
   * Utility: product subtotal price dynamic calculation for product selector
   */
  function initializeProductPricing() {
    const productCards = document.querySelectorAll(".product-card");
    productCards.forEach((card) => {
      const qtyInput = card.querySelector(".product-qty__input");
      const qtyIncrease = card.querySelector(".qty-increase");
      const qtyDecrease = card.querySelector(".qty-decrease");
      const subtotalSpan = card.querySelector(".subtotal-amount");
      if (qtyInput && subtotalSpan) {
        const unitPrice = parseFloat(qtyInput.dataset.price) / 100;
        function updateSubtotal() {
          const quantity = parseInt(qtyInput.value, 10) || 1;
          const subtotal = (unitPrice * quantity).toFixed(2);
          subtotalSpan.textContent = subtotal;
        }
        qtyInput.addEventListener("input", updateSubtotal);
        qtyInput.addEventListener("change", updateSubtotal);
        if (qtyIncrease) {
          qtyIncrease.addEventListener("click", () =>
            setTimeout(updateSubtotal, 10),
          );
        }
        if (qtyDecrease) {
          qtyDecrease.addEventListener("click", () =>
            setTimeout(updateSubtotal, 10),
          );
        }
        updateSubtotal();
      }
    });
  }

  /**
   * Utility: drag and drop file upload UI/controller
   */
  function initializeDragDropUpload() {
    const dragDropContainers = document.querySelectorAll(".drag-drop-upload");
    dragDropContainers.forEach((container) => {
      const dropArea = container.querySelector(".drag-drop-upload__area");
      const fileInput = container.querySelector(".drag-drop-upload__input");
      const browseBtn = container.querySelector(".drag-drop-upload__browse");
      const selectedArea = container.querySelector(
        ".drag-drop-upload__selected",
      );
      const fileName = container.querySelector(".drag-drop-upload__file-name");
      const fileSize = container.querySelector(".drag-drop-upload__file-size");
      const removeBtn = container.querySelector(".drag-drop-upload__remove");
      const statusDiv = document.getElementById(
        container.dataset.fieldId + "-status",
      );
      const maxSize = parseInt(container.dataset.maxSize, 10) * 1024 * 1024;
      function preventDefaults(e) {
        e.preventDefault();
        e.stopPropagation();
      }
      ["dragenter", "dragover", "dragleave", "drop"].forEach((eventName) => {
        dropArea.addEventListener(eventName, preventDefaults, false);
        document.body.addEventListener(eventName, preventDefaults, false);
      });
      ["dragenter", "dragover"].forEach((eventName) => {
        dropArea.addEventListener(
          eventName,
          () => dropArea.classList.add("drag-over"),
          false,
        );
      });
      ["dragleave", "drop"].forEach((eventName) => {
        dropArea.addEventListener(
          eventName,
          () => dropArea.classList.remove("drag-over"),
          false,
        );
      });
      dropArea.addEventListener("drop", handleDrop, false);
      browseBtn.addEventListener("click", () => fileInput.click());
      fileInput.addEventListener("change", handleFileSelect);
      removeBtn.addEventListener("click", removeFile);
      function handleDrop(e) {
        const dt = e.dataTransfer;
        const files = dt.files;
        handleFiles(files);
      }
      function handleFileSelect(e) {
        const files = e.target.files;
        handleFiles(files);
      }
      function handleFiles(files) {
        if (files.length > 0) {
          const file = files[0];
          if (file.size > maxSize) {
            showError(
              `File size must be less than ${container.dataset.maxSize}MB`,
            );
            return;
          }
          const acceptTypes = container.dataset.accept;
          if (
            acceptTypes &&
            !acceptTypes
              .split(",")
              .map((t) => t.trim())
              .includes(file.type)
          ) {
            showError(
              "File type not allowed. Please check the accepted formats.",
            );
            return;
          }
          fileName.textContent = file.name;
          fileSize.textContent = formatFileSize(file.size);
          dropArea.style.display = "none";
          selectedArea.style.display = "flex";
          hideStatus();
        }
      }
      function removeFile() {
        fileInput.value = "";
        dropArea.style.display = "flex";
        selectedArea.style.display = "none";
        hideStatus();
      }
      function showError(message) {
        if (statusDiv) {
          statusDiv.textContent = message;
          statusDiv.className = "upload__status upload__status--error";
          statusDiv.style.display = "block";
        }
      }
      function hideStatus() {
        if (statusDiv) statusDiv.style.display = "none";
      }
      function formatFileSize(bytes) {
        if (bytes === 0) return "0 Bytes";
        const k = 1024,
          sizes = ["Bytes", "KB", "MB", "GB"];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
      }
    });
  }

  /**
   * Bootstrap from Liquid: call this in the template to initialize everything.
   */
  function initLiquid(configLiquid) {
    // Save config for possible later use
    config = Object.assign(
      {
        sectionId: "",
        testMode: false,
        devMode: false,
      },
      configLiquid,
    );

    // Main shared form stuff
    try {
      window.CustomForm.init(config);
    } catch (e) {
      console.warn("CustomForm initialization failed:", e);
    }

    // Call dynamic helpers for product price and upload interaction
    initializeProductPricing();
    initializeDragDropUpload();
  }

  /**
   * Main initialization and public interface
   */
  return {
    init: function (userConfig) {
      config = Object.assign(
        {
          sectionId: "",
          testMode: false,
          devMode: false,
        },
        userConfig,
      );

      // Initialize all components
      try {
        FormState.init();
        ProductSelector.init();
      } catch (error) {
        console.error("CustomForm initialization error:", error);
      }
    },

    // Expose as required by bootstrap (Liquid)
    initLiquid: initLiquid,
    initializeProductPricing: initializeProductPricing,
    initializeDragDropUpload: initializeDragDropUpload,

    // Expose utilities for external use
    utils: FormUtils,

    // Get current config
    getConfig: function () {
      return config;
    },

    // Expose form state methods
    clearSavedData: function () {
      FormState.clearFormData();
    },

    showError: function (message) {
      FormState.announceError(document.activeElement, message);
    },

    setLoadingState: function (loading) {
      FormState.showLoadingState(loading);
    },

    // Handle form submission after reCAPTCHA validation
    handleSubmit: function (token, configData) {
      const form = document.getElementById("form-" + configData.sectionId);
      if (!form) {
        console.error("Form not found for submission");
        return;
      }

      try {
        // Set mode based on test setting
        const modeInput = document.getElementById(
          "mode-" + configData.sectionId,
        );
        if (modeInput) {
          modeInput.value = configData.testMode ? "test" : "production";
        }

        // Add submission UUID
        FormUtils.addSubmissionUUID(form);

        // Clean up unselected products
        FormUtils.cleanupUnselectedProducts();

        // Show loading state
        FormState.showLoadingState(true);

        // Create FormData object (this will include the reCAPTCHA token automatically)
        const formData = new FormData(form);

        // Get form action URL
        const actionUrl = configData.testMode
          ? form.dataset.actionTest || form.action
          : form.action;

        // Submit via fetch to prevent page redirect
        fetch(actionUrl, {
          method: "POST",
          body: formData,
        })
          .then((response) => {
            FormState.showLoadingState(false);

            if (response.ok) {
              // Clear saved form data on successful submission
              FormState.clearFormData();

              // Get the generated UUID
              const uuidInput = form.querySelector(
                'input[name="submission_uuid"]',
              );
              const generatedUuid = uuidInput ? uuidInput.value : "";

              // Hide the form and show success message
              form.style.display = "none";
              const successMessage = document.getElementById(
                "form-message-" + configData.sectionId,
              );
              if (successMessage) {
                // Replace %uuid% placeholder with actual UUID
                if (generatedUuid) {
                  const nonFormSections =
                    document.getElementsByClassName("shopify-section section");
                  for (const section of nonFormSections) {
                    if (section.id !== 'shopify-section-' + configData.sectionId) {
                      section.style.display = "none";
                    }
                  }
                  const messageContent = successMessage.innerHTML;
                  successMessage.innerHTML = messageContent.replace(
                    /%uuid%/g,
                    generatedUuid,
                  );
                }
                successMessage.style.display = "block";
              }

              // Reset reCAPTCHA
              if (window.grecaptcha) {
                window.grecaptcha.reset();
              }
            } else {
              throw new Error("Form submission failed");
            }
          })
          .catch((error) => {
            console.error("Form submission error:", error);
            FormState.showLoadingState(false);

            // Show error message
            const statusDiv = document.getElementById(
              "submit-" + configData.sectionId + "-status",
            );
            if (statusDiv) {
              statusDiv.textContent =
                "An error occurred while submitting the form. Please try again.";
              statusDiv.className =
                "form-submit-status form-submit-status--error";
              statusDiv.style.display = "block";
            }

            // Reset reCAPTCHA so user can try again
            if (window.grecaptcha) {
              window.grecaptcha.reset();
            }
          });
      } catch (error) {
        console.error("Form submission setup error:", error);
        FormState.showLoadingState(false);

        // Show error message
        const statusDiv = document.getElementById(
          "submit-" + configData.sectionId + "-status",
        );
        if (statusDiv) {
          statusDiv.textContent =
            "An error occurred while submitting the form. Please try again.";
          statusDiv.className = "form-submit-status form-submit-status--error";
          statusDiv.style.display = "block";
        }
      }
    },
  };
})();
